/**
 * Shared DataGrid filter operators for MUI X DataGrid.
 * Use for amount columns: numericFilterOperators (contains, not like, plus defaults).
 * Use for string columns: stringFilterOperators (not like, !=, >=, <=, plus defaults).
 * Use for date columns: dateFilterOperators (not like, in month, in month range, plus defaults).
 *
 * @param {Function} t - i18n translation function (e.g. from useTranslation())
 */
import React from 'react';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import {
    getGridStringOperators,
    getGridNumericOperators,
    getGridDateOperators,
    GridFilterInputValue,
} from '@mui/x-data-grid';
import { parseBeautifiedNumber } from './FormatNumber';

import 'dayjs/locale/en';
import 'dayjs/locale/ar';
import 'dayjs/locale/he';

/** Map i18next language to dayjs locale for month/year picker labels */
const dayjsLocaleFromI18n = (lang) => {
    if (!lang) return 'en';
    const base = String(lang).split('-')[0].toLowerCase();
    if (base === 'he') return 'he';
    if (base === 'ar') return 'ar';
    return 'en';
};

export const getGridStringOperatorsWithComparison = (t) => [
    ...getGridStringOperators(),
    {
        value: 'notLike',
        label: t('FilterNotLike'),
        getApplyFilterFn: (filterItem) => {
            if (filterItem.value == null || filterItem.value === '') return null;
            const search = String(filterItem.value ?? '').toLowerCase();
            return (value) => !String(value ?? '').toLowerCase().includes(search);
        },
    },
    {
        value: '!=',
        label: t('FilterNotEquals'),
        getApplyFilterFn: (filterItem) => {
            if (filterItem.value == null || filterItem.value === '') return null;
            return (value) => (value ?? '') !== (filterItem.value ?? '');
        },
    },
    {
        value: '>=',
        label: t('FilterGreaterThanOrEqual'),
        getApplyFilterFn: (filterItem) => {
            if (filterItem.value == null || filterItem.value === '') return null;
            return (value) => String(value ?? '') >= String(filterItem.value ?? '');
        },
    },
    {
        value: '<=',
        label: t('FilterLessThanOrEqual'),
        getApplyFilterFn: (filterItem) => {
            if (filterItem.value == null || filterItem.value === '') return null;
            return (value) => String(value ?? '') <= String(filterItem.value ?? '');
        },
    },
];

const applyNumericCompare = (filterItem, compare) => {
    if (filterItem.value == null || filterItem.value === '') return null;
    const filterNum = parseBeautifiedNumber(filterItem.value);
    if (filterNum == null) return null;
    return (value) => {
        const cellNum = parseBeautifiedNumber(value);
        if (cellNum == null) return false;
        return compare(cellNum, filterNum);
    };
};

const numericSearchText = (value) => {
    const n = parseBeautifiedNumber(value);
    if (n != null) return String(n);
    return String(value ?? '').replace(/[(),\s]/g, '').toLowerCase();
};

/**
 * Numeric filter operators: default + contains, not like.
 * Parses beautified amounts ("1,980.00", "(1,240.00)") so equals/compare work
 * on formatted bank/book amount cells, not just raw numbers.
 */
export const getGridNumericOperatorsWithNotLike = (t) => [
    ...getGridNumericOperators().map((op) => {
        switch (op.value) {
            case '=':
                return { ...op, getApplyFilterFn: (item) => applyNumericCompare(item, (a, b) => a === b) };
            case '!=':
                return { ...op, getApplyFilterFn: (item) => applyNumericCompare(item, (a, b) => a !== b) };
            case '>':
                return { ...op, getApplyFilterFn: (item) => applyNumericCompare(item, (a, b) => a > b) };
            case '>=':
                return { ...op, getApplyFilterFn: (item) => applyNumericCompare(item, (a, b) => a >= b) };
            case '<':
                return { ...op, getApplyFilterFn: (item) => applyNumericCompare(item, (a, b) => a < b) };
            case '<=':
                return { ...op, getApplyFilterFn: (item) => applyNumericCompare(item, (a, b) => a <= b) };
            case 'isAnyOf':
                return {
                    ...op,
                    getApplyFilterFn: (filterItem) => {
                        if (!Array.isArray(filterItem.value) || filterItem.value.length === 0) return null;
                        const wanted = filterItem.value
                            .map(parseBeautifiedNumber)
                            .filter((n) => n != null);
                        if (!wanted.length) return null;
                        return (value) => {
                            const cellNum = parseBeautifiedNumber(value);
                            return cellNum != null && wanted.includes(cellNum);
                        };
                    },
                };
            default:
                return op;
        }
    }),
    {
        value: 'contains',
        label: t('FilterContains'),
        InputComponent: GridFilterInputValue,
        InputComponentProps: { type: 'text' },
        getApplyFilterFn: (filterItem) => {
            if (filterItem.value == null || filterItem.value === '') return null;
            const search = numericSearchText(filterItem.value);
            if (!search) return null;
            return (value) => numericSearchText(value).includes(search);
        },
    },
    {
        value: 'notLike',
        label: t('FilterNotLike'),
        InputComponent: GridFilterInputValue,
        InputComponentProps: { type: 'text' },
        getApplyFilterFn: (filterItem) => {
            if (filterItem.value == null || filterItem.value === '') return null;
            const search = numericSearchText(filterItem.value);
            if (!search) return null;
            return (value) => !numericSearchText(value).includes(search);
        },
    },
];

// Convert any date-like value coming from the row (Date | ISO string | etc.) into a
// {year, month} pair (month is 0-indexed) suitable for comparison.
const getYearMonthFromCellValue = (value) => {
    if (value == null) return null;
    const date = value instanceof Date ? value : new Date(value);
    if (isNaN(date.getTime())) return null;
    return { year: date.getFullYear(), month: date.getMonth() };
};

// Parse a "YYYY-MM" string (from month/year picker) into {year, month}.
const parseYearMonthString = (value) => {
    if (!value || typeof value !== 'string') return null;
    const [yearStr, monthStr] = value.split('-');
    const year = parseInt(yearStr, 10);
    const month = parseInt(monthStr, 10);
    if (isNaN(year) || isNaN(month)) return null;
    return { year, month: month - 1 };
};

// Convert {year, month} to a single comparable integer (months since year 0).
const toYearMonthOrdinal = ({ year, month }) => year * 12 + month;

const ymToDayjs = (ym) => {
    if (!ym) return null;
    const parsed = parseYearMonthString(typeof ym === 'string' ? ym : '');
    if (!parsed) return null;
    const d = dayjs().year(parsed.year).month(parsed.month).date(1);
    return d.isValid() ? d : null;
};

/** Month/year picker localized via dayjs (Hebrew month names when UI is Hebrew). */
const GridFilterInputMonthSingle = (props) => {
    const { item, applyValue, focusElementRef } = props;
    const { i18n } = useTranslation();
    const adapterLocale = dayjsLocaleFromI18n(i18n.language);

    const strVal = typeof item.value === 'string' ? item.value : '';
    const value = ymToDayjs(strVal);

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={adapterLocale}>
            <DatePicker
                views={['year', 'month']}
                openTo="month"
                value={value}
                onChange={(newVal) => {
                    applyValue({
                        ...item,
                        value:
                            newVal != null && newVal.isValid()
                                ? newVal.format('YYYY-MM')
                                : undefined,
                    });
                }}
                slotProps={{
                    textField: {
                        variant: 'standard',
                        fullWidth: true,
                        inputRef: focusElementRef,
                    },
                }}
            />
        </LocalizationProvider>
    );
};

// Custom filter input: from/to month with localized month & year labels.
// Value is stored as ["YYYY-MM", "YYYY-MM"].
const GridFilterInputMonthRange = (props) => {
    const { item, applyValue, focusElementRef } = props;
    const { i18n } = useTranslation();
    const adapterLocale = dayjsLocaleFromI18n(i18n.language);

    const rawValue = Array.isArray(item.value) ? item.value : ['', ''];
    const startStr = rawValue[0] ?? '';
    const endStr = rawValue[1] ?? '';

    const commit = (nextStart, nextEnd) => {
        applyValue({ ...item, value: [nextStart, nextEnd] });
    };

    const dir = i18n.dir();

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={adapterLocale}>
            <div
                style={{
                    display: 'flex',
                    gap: 8,
                    alignItems: 'flex-end',
                    width: '100%',
                    flexDirection: dir === 'rtl' ? 'row-reverse' : 'row',
                }}
            >
                <DatePicker
                    views={['year', 'month']}
                    openTo="month"
                    value={ymToDayjs(startStr)}
                    onChange={(newVal) => {
                        const next =
                            newVal != null && newVal.isValid()
                                ? newVal.format('YYYY-MM')
                                : '';
                        commit(next, endStr);
                    }}
                    slotProps={{
                        textField: {
                            variant: 'standard',
                            fullWidth: true,
                            inputRef: focusElementRef,
                        },
                    }}
                />
                <DatePicker
                    views={['year', 'month']}
                    openTo="month"
                    value={ymToDayjs(endStr)}
                    onChange={(newVal) => {
                        const next =
                            newVal != null && newVal.isValid()
                                ? newVal.format('YYYY-MM')
                                : '';
                        commit(startStr, next);
                    }}
                    slotProps={{
                        textField: {
                            variant: 'standard',
                            fullWidth: true,
                        },
                    }}
                />
            </div>
        </LocalizationProvider>
    );
};

export const getGridDateOperatorsWithNotLike = (t) => [
    ...getGridDateOperators(),
    {
        value: 'notLike',
        label: t('FilterNotLike'),
        getApplyFilterFn: (filterItem) => {
            if (filterItem.value == null || filterItem.value === '') return null;
            const filterDate = filterItem.value instanceof Date ? filterItem.value.getTime() : new Date(filterItem.value).getTime();
            return (value) => {
                if (value == null) return true;
                const cellTime = value instanceof Date ? value.getTime() : new Date(value).getTime();
                return cellTime !== filterDate;
            };
        },
    },
    {
        value: 'inMonth',
        label: t('FilterInMonth'),
        InputComponent: GridFilterInputMonthSingle,
        getApplyFilterFn: (filterItem) => {
            const ym = parseYearMonthString(filterItem.value);
            if (!ym) return null;
            const target = toYearMonthOrdinal(ym);
            return (value) => {
                const cellYm = getYearMonthFromCellValue(value);
                if (!cellYm) return false;
                return toYearMonthOrdinal(cellYm) === target;
            };
        },
    },
    {
        value: 'inMonthRange',
        label: t('FilterInMonthRange'),
        InputComponent: GridFilterInputMonthRange,
        getApplyFilterFn: (filterItem) => {
            if (!Array.isArray(filterItem.value)) return null;
            const startYm = parseYearMonthString(filterItem.value[0]);
            const endYm = parseYearMonthString(filterItem.value[1]);
            if (!startYm && !endYm) return null;
            const startOrdinal = startYm ? toYearMonthOrdinal(startYm) : -Infinity;
            const endOrdinal = endYm ? toYearMonthOrdinal(endYm) : Infinity;
            const lo = Math.min(startOrdinal, endOrdinal);
            const hi = Math.max(startOrdinal, endOrdinal);
            return (value) => {
                const cellYm = getYearMonthFromCellValue(value);
                if (!cellYm) return false;
                const cellOrdinal = toYearMonthOrdinal(cellYm);
                return cellOrdinal >= lo && cellOrdinal <= hi;
            };
        },
    },
];
